/**
 * Cross-tab deal update notification — Feb 2026 Cleanup Ship.
 *
 * Uses BroadcastChannel API (with localStorage fallback for older browsers)
 * so that when a deal is PATCHed in one tab (e.g. a focused resolution modal),
 * other tabs subscribed to deal updates (e.g. the Data Health "Missing
 * Integrity Fields" worklist in a different tab) auto-refresh without a
 * manual reload.
 *
 * Producer:
 *   import { emitDealUpdate } from 'hooks/useDealUpdates';
 *   emitDealUpdate({ dealId, source: 'resolve-commission-paid' });
 *
 * Consumer (e.g. inside MissingIntegrityFieldsSection):
 *   useDealUpdates(() => refetch());
 */
import { useEffect } from 'react';

const CHANNEL_NAME = 'pfs.deal.updates';
const STORAGE_KEY = 'pfs.deal.updates.fallback';

const supportsBroadcastChannel = typeof window !== 'undefined' && 'BroadcastChannel' in window;

let _channel = null;
function _getChannel() {
  if (!supportsBroadcastChannel) return null;
  if (!_channel) {
    _channel = new BroadcastChannel(CHANNEL_NAME);
  }
  return _channel;
}

/** Emits a deal-update message to subscribers across tabs. */
export function emitDealUpdate(payload = {}) {
  const msg = { ts: Date.now(), ...payload };
  const ch = _getChannel();
  if (ch) {
    try { ch.postMessage(msg); } catch (_e) { /* no-op */ }
  } else if (typeof window !== 'undefined') {
    // localStorage fallback — write then immediately remove so the next
    // write triggers a fresh `storage` event even for the same payload.
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(msg));
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (_e) { /* no-op */ }
  }
}

/**
 * Subscribes to cross-tab deal updates. Calls `onUpdate(msg)` for each event.
 * Returns nothing — the hook handles teardown automatically on unmount.
 */
export default function useDealUpdates(onUpdate) {
  useEffect(() => {
    if (typeof onUpdate !== 'function') return undefined;

    if (supportsBroadcastChannel) {
      const ch = new BroadcastChannel(CHANNEL_NAME);
      const handler = (e) => { onUpdate(e.data || {}); };
      ch.addEventListener('message', handler);
      return () => {
        ch.removeEventListener('message', handler);
        ch.close();
      };
    }

    // localStorage fallback
    const handler = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try { onUpdate(JSON.parse(e.newValue)); } catch (_e) { onUpdate({}); }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [onUpdate]);
}
